import { $axios } from './axios-instance';
import convertToFormDataObject from '@/utilities/convert-to-form-data';

let API_URL = process.env.VUE_APP_FULFILLMENT_API_BASE_URL;

const state = {
    userEmail: '',
    isUserLoggedIn: undefined,
    preferences: {
        isBusinessCustomer: {
            value: null,
            isSet: false,
        },
        acceptAttachmentDisclaimer: {
            value: null,
            isSet: false,
        },
    },
    userAccountDetails: {},
    ordersList: [],
};

const mutations = {
    SET_USER_ACCOUNT_DETAILS(state, cartItem) {
        state.userAccountDetails = cartItem;
    },
    SET_MR_SESSION_ID(state, mrSessionId) {
        state.mrSessionId = mrSessionId;
    },
    SET_ORDERS_LIST(state, ordersList) {
        state.ordersList = ordersList;
    },
    SET_ORDER_ITEM(state, oderItem) {
        state.orderItem = oderItem;
    },
    SET_LOGIN_STATE(state, login) {
        state.isUserLoggedIn = login;
    },
    SET_USER_EMAIL(state, email) {
        state.userEmail = email;
    },
    SET_USER_PREFERENCES(state, preferences) {
        state.preferences = preferences;
        localStorage.setItem(
            'userPreferences',
            JSON.stringify(state.preferences)
        );
    },
};

const actions = {
    updateUserAccountDetails: async ({ commit }, userDetails) => {
        try {
            const response = await $axios.put(
                `${API_URL}/auth/user/details`,
                userDetails,
                {
                    headers: {
                        'x-api-key': process.env.VUE_APP_FULLFILLMENT_API_KEY,
                    },
                    withCredentials: true,
                }
            );
            commit('SET_USER_ACCOUNT_DETAILS', response.data);
            return response.data;
        } catch (e) {
            console.log(e);
            throw e;
        }
    },
    updateUserEmail: ({ commit }, email) => {
        commit('SET_USER_EMAIL', email);
    },
    fetchUserAccountDetails: async ({ commit, state }) => {
        try {
            const response = await $axios.get(`${API_URL}/auth/user/details`, {
                headers: {
                    'x-api-key': process.env.VUE_APP_FULLFILLMENT_API_KEY,
                },
                withCredentials: true,
            });
            commit('SET_USER_ACCOUNT_DETAILS', response.data);
            return response.data;
        } catch (e) {
            console.log(e);
        }
    },
    loginUser: async ({ commit, state, dispatch }, { credentials }) => {
        try {
            await $axios.post(`${API_URL}/auth/signin`, credentials, {
                headers: {
                    'x-api-key': process.env.VUE_APP_FULLFILLMENT_API_KEY,
                },
                withCredentials: true,
            });
            await dispatch('setLoginState', true);
            await dispatch('clearPaymentDetails');
            await dispatch('fetchUserAccountDetails');
        } catch (e) {
            console.log(e);
        }
    },
    async checkAuth({ commit, dispatch }) {
        try {
            const response = await $axios.post(
                '/auth/refresh',
                {},
                { withCredentials: true }
            );
            if (response.status === 201) {
                commit('SET_LOGIN_STATE', true);
                dispatch('fetchUserAccountDetails');
            }
        } catch (e) {
            commit('SET_LOGIN_STATE', false);
        }
    },
    logoutUser: async ({ commit, dispatch }) => {
        await $axios.post(
            `${API_URL}/auth/logout`,
            {},
            {
                withCredentials: true,
                headers: {
                    'x-api-key': process.env.VUE_APP_FULLFILLMENT_API_KEY,
                },
            }
        );
        commit('SET_USER_ACCOUNT_DETAILS', {});
        await dispatch('clearPaymentDetails');
        dispatch('setLoginState', false);
    },
    registerUser: async ({ commit, state, dispatch }, credentials) => {
        await $axios.post(`${API_URL}/auth/signup`, credentials, {
            headers: { 'x-api-key': process.env.VUE_APP_FULLFILLMENT_API_KEY },
        });
    },
    optinUser: async ({ commit, state }, optinId) => {
        await $axios.post(
            `${API_URL}/auth/optin`,
            { uuid: optinId },
            {
                headers: {
                    'x-api-key': process.env.VUE_APP_FULLFILLMENT_API_KEY,
                },
            }
        );
    },
    sendPasswordResetLink: async ({ commit, state }, { email }) => {
        try {
            const response = await $axios.post(
                `${API_URL}/auth/reset-password`,
                { email },
                {
                    headers: {
                        'x-api-key': process.env.VUE_APP_FULLFILLMENT_API_KEY,
                    },
                }
            );
        } catch (e) {
            console.log(e);
        }
    },
    resetPassword: async ({ commit, state }, { resetData }) => {
        await $axios.post(`${API_URL}/auth/reset-password/callback`, resetData);
    },
    changePassword: async ({ commit, dispatch }, { resetData }) => {
        try {
            await $axios.post(`${API_URL}/auth/password`, resetData, {
                withCredentials: true,
                headers: {
                    'x-api-key': process.env.VUE_APP_FULLFILLMENT_API_KEY,
                },
            });
            dispatch('logoutUser');
        } catch (e) {
            console.log(e);
        }
    },
    changeEmail: async ({ commit, state }, newMail) => {
        try {
            await $axios.post(`${API_URL}/auth/email`, newMail, {
                withCredentials: true,
                headers: {
                    'x-api-key': process.env.VUE_APP_FULLFILLMENT_API_KEY,
                },
            });
        } catch (e) {
            console.log(e);
        }
    },
    fetchOrdersList: ({ commit, state }) => {
        return new Promise((resolve, reject) => {
            if (Object.entries(state.ordersList).length === 0) {
                $axios
                    .get(API_URL + '/ProfileOrdersGet.php', {
                        headers: state.mrSessionId,
                        params: {
                            date: new Date().getTime(),
                        },
                    })
                    .then((response) => {
                        commit('SET_ORDERS_LIST', response.data.list);
                        resolve(response.data.list);
                    });
            }
        });
    },
    setLoginState: ({ commit }, login) => {
        localStorage.setItem('loggedIn', login);
        commit('SET_LOGIN_STATE', login);
    },
    updateUserPreferences: ({ commit, state }, preferences) => {
        commit('SET_USER_PREFERENCES', {
            ...state.preferences,
            ...preferences,
        });
    },
};

const getters = {
    userAccountDetails: (state) => {
        return state.userAccountDetails;
    },
    userPreferences: (state) => {
        return state.preferences;
    },
    isBusinessCustomer: (state) => {
        /** Only logged in users, Company is verified */
        if (state.preferences.isBusinessCustomer.value) {
            return true;
        }

        if (
            state.isUserLoggedIn &&
            state.userAccountDetails.billingInformation
        ) {
            return (
                state.userAccountDetails.billingInformation.isVerifiedCompany ||
                false
            );
        }
        return false;
    },
    ordersList: (state) => {
        return state.ordersList;
    },
    orderItem: (state) => {
        return state.orderItem;
    },
    isUserLoggedIn: (state) => {
        return state.isUserLoggedIn;
    },
    userEmail: (state) => {
        return state.userEmail;
    },
};

export default { state, mutations, actions, getters };
